'use client'

import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getDateFnsLocale } from '@/lib/utils/date-locale'
import { cn } from '@/lib/utils/index'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}

const monthLabels = (locale: string) =>
  Array.from({ length: 12 }, (_, month) =>
    new Date(2000, month, 1).toLocaleString(locale, { month: 'short' })
  )
// Without a startMonth / endMonth from the caller, the year picker spans this many years
// either side of the shown month.
const DEFAULT_YEAR_SPAN = 10

const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth()

/**
 * The calendar's header row — ‹ month year › — owned here rather than by DayPicker: the
 * shown month is our state, handed to DayPicker as `month`, so a pick from either Select
 * moves the grid directly instead of round-tripping through DayPicker's native-<select>
 * dropdown events. `captionLayout="dropdown"` shows the month and year as our Select (the
 * theme's menu surface); `"label"` shows them as text.
 */
function CalendarHeader({
  month,
  onMonthChange,
  startMonth,
  endMonth,
  dropdown,
}: {
  month: Date
  onMonthChange: (month: Date) => void
  startMonth?: Date
  endMonth?: Date
  dropdown: boolean
}) {
  const locale = useLocale()
  const t = useTranslations('common')
  const labels = React.useMemo(() => monthLabels(locale), [locale])
  const shown = monthIndex(month)
  const first = startMonth ? monthIndex(startMonth) : -Infinity
  const last = endMonth ? monthIndex(endMonth) : Infinity
  const go = (index: number) => {
    const clamped = Math.min(last, Math.max(first, index))
    onMonthChange(new Date(Math.floor(clamped / 12), clamped % 12, 1))
  }

  const year = month.getFullYear()
  const firstYear = startMonth?.getFullYear() ?? year - DEFAULT_YEAR_SPAN
  const lastYear = endMonth?.getFullYear() ?? year + DEFAULT_YEAR_SPAN
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => lastYear - i)

  return (
    <div className="flex h-7 items-center justify-between gap-1">
      <Button
        type="button"
        variant="ghost"
        size="iconXs"
        aria-label={t('previousMonth')}
        disabled={shown <= first}
        onClick={() => go(shown - 1)}
      >
        <ChevronLeftIcon />
      </Button>
      {dropdown ? (
        <div className="flex items-center gap-1">
          <Select
            value={String(month.getMonth())}
            onValueChange={(value) => go(year * 12 + Number(value))}
          >
            <SelectTrigger size="xs" aria-label={t('month')} className="w-auto gap-1 px-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64 min-w-20">
              {labels.map((label, index) => {
                const target = year * 12 + index
                return (
                  <SelectItem
                    key={label}
                    value={String(index)}
                    disabled={target < first || target > last}
                    className="text-xs"
                  >
                    {label}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          <Select
            value={String(year)}
            // Same month in the new year, pulled back inside the range if it falls outside.
            onValueChange={(value) => go(Number(value) * 12 + month.getMonth())}
          >
            <SelectTrigger size="xs" aria-label={t('year')} className="w-auto gap-1 px-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64 min-w-20">
              {years.map((value) => (
                <SelectItem key={value} value={String(value)} className="text-xs">
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <span className="text-xs font-medium select-none">
          {month.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}
        </span>
      )}
      <Button
        type="button"
        variant="ghost"
        size="iconXs"
        aria-label={t('nextMonth')}
        disabled={shown >= last}
        onClick={() => go(shown + 1)}
      >
        <ChevronRightIcon />
      </Button>
    </div>
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant: _buttonVariant = 'ghost',
  formatters,
  components,
  month: controlledMonth,
  defaultMonth,
  onMonthChange,
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames()
  const locale = useLocale()
  const selected = 'selected' in props ? props.selected : undefined
  const [internalMonth, setInternalMonth] = React.useState<Date>(
    () => defaultMonth ?? (selected instanceof Date ? selected : undefined) ?? new Date()
  )
  const month = controlledMonth ?? internalMonth
  const changeMonth = (next: Date) => {
    setInternalMonth(next)
    onMonthChange?.(next)
  }

  return (
    <div
      data-slot="calendar"
      className={cn(
        // No fill of its own: it sits on whatever surface hosts it (a popover's menu-surface),
        // rather than drawing a second, darker box inside it.
        'flex w-fit flex-col gap-2 p-3 [--cell-size:2rem]',
        className
      )}
    >
      <CalendarHeader
        month={month}
        onMonthChange={changeMonth}
        startMonth={props.startMonth}
        endMonth={props.endMonth}
        dropdown={captionLayout !== 'label'}
      />
      <DayPicker
        showOutsideDays={showOutsideDays}
        month={month}
        onMonthChange={changeMonth}
        // The header above is the navigation; DayPicker's own caption stays for screen readers.
        hideNavigation
        // DayPicker's own labels (day buttons, the screen-reader caption) and week start.
        locale={getDateFnsLocale(locale)}
        formatters={{
          formatWeekdayName: (date) => date.toLocaleDateString(locale, { weekday: 'narrow' }),
          ...formatters,
        }}
        classNames={{
          root: cn(defaultClassNames.root),
          months: cn('flex flex-col', defaultClassNames.months),
          month: cn('flex w-full flex-col', defaultClassNames.month),
          month_caption: 'sr-only',
          table: 'w-full border-collapse',
          weekdays: cn('flex', defaultClassNames.weekdays),
          weekday: cn(
            'text-foreground/50 w-(--cell-size) pb-1 text-center text-xs font-normal select-none',
            defaultClassNames.weekday
          ),
          week: cn('flex w-full', defaultClassNames.week),
          week_number_header: cn(
            'w-(--cell-size) select-none',
            defaultClassNames.week_number_header
          ),
          week_number: cn('text-foreground/50 text-xs select-none', defaultClassNames.week_number),
          day: cn(
            'group/day relative size-(--cell-size) p-0 text-center select-none',
            defaultClassNames.day
          ),
          range_start: cn('bg-foreground/10', defaultClassNames.range_start),
          range_middle: cn('rounded-none', defaultClassNames.range_middle),
          range_end: cn('bg-foreground/10', defaultClassNames.range_end),
          today: cn(
            '[&_button]:underline [&_button]:decoration-foreground/40 [&_button]:underline-offset-4',
            defaultClassNames.today
          ),
          outside: cn('text-foreground/40', defaultClassNames.outside),
          disabled: cn('text-foreground/20', defaultClassNames.disabled),
          hidden: cn('invisible', defaultClassNames.hidden),
          ...classNames,
        }}
        components={{
          DayButton: CalendarDayButton,
          WeekNumber: ({ children, ...props }) => {
            return (
              <td {...props}>
                <div className="flex size-(--cell-size) items-center justify-center text-center">
                  {children}
                </div>
              </td>
            )
          },
          ...components,
        }}
        {...props}
      />
    </div>
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  const selectedSingle =
    modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="iconXs"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={selectedSingle}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      // The chosen day is a solid inverse fill: the translucent bg-emphasis the dark themes
      // use for chips is too faint to find a single cell by in a 6×7 grid. Keyboard focus is
      // the app's one global outline (STYLE_GUIDE §3), not a ring here.
      className={cn(
        'size-(--cell-size) text-xs font-normal',
        'data-[selected-single=true]:bg-foreground data-[selected-single=true]:text-background data-[selected-single=true]:hover:bg-foreground data-[selected-single=true]:hover:text-background data-[selected-single=true]:font-medium',
        'data-[range-start=true]:bg-foreground! data-[range-start=true]:text-background',
        'data-[range-end=true]:bg-foreground! data-[range-end=true]:text-background',
        'data-[range-middle=true]:bg-foreground/10 data-[range-middle=true]:text-foreground data-[range-middle=true]:rounded-none',
        modifiers.disabled && 'pointer-events-none',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
